import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import {
  CardFieldsBand,
  CardFieldsSkeleton,
} from "@/components/card-fields-skeleton";
import { EmptyState } from "@/components/empty-state";
import { cx } from "@/utils/cx";
import {
  fontSource,
  stripeAppearance,
  stripeLocale,
} from "@/stripe/appearance";
import type { PortalPaymentMethod } from "@/data/portal";
import type { Stripe } from "@stripe/stripe-js";

/**
 * How the client pays (template detail 1.7, body; spec row-52-default 1.2).
 *
 * One row per card: the brand as a WORD, the last four, the expiry, and either the word Default
 * or the one control that makes it the default. Never the card brand image from
 * Stripe's CDN: four logos in four palettes on a page whose one colour is the primary action is
 * four more brands than the page can carry, and the word is what a person says out loud anyway.
 *
 * Adding a card opens the setup form under the rows, and while it is open the section header's
 * own button is hidden and Save card is the page's one orange.
 */

const BRAND_WORDS: Record<string, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
};

export interface PaymentMethodsSectionProps {
  methods: PortalPaymentMethod[];
  /** Which card is being made the default right now; its control keeps its label. */
  settingDefaultId: string | null;
  onSetDefault: (paymentMethodId: string) => void;
  adding: boolean;
  setupClientSecret: string | null;
  stripe: Stripe | null;
  coachHex?: string | null;
  language: string;
  onOpenAdd: () => void;
  onCloseAdd: () => void;
  onAdded: () => void;
  error: string | null;
}

export function PaymentMethodsSection({
  methods,
  settingDefaultId,
  onSetDefault,
  adding,
  setupClientSecret,
  stripe,
  coachHex,
  language,
  onOpenAdd,
  onCloseAdd,
  onAdded,
  error,
}: PaymentMethodsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="gap-gk-4 flex flex-col">
      {error && <Alert tone="danger">{error}</Alert>}

      {methods.length === 0 && !adding ? (
        <EmptyState
          heading={t("gymkeePay.portal.noCard")}
          action={
            <Button
              variant="secondary"
              className="ds-pay-hand"
              onClick={onOpenAdd}
            >
              {t("gymkeePay.portal.addACard")}
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col">
          {methods.map((method) => {
            const card = method.card;
            const brand = card
              ? (BRAND_WORDS[card.brand.toLowerCase()] ?? null)
              : null;
            return (
              /* A card with an expiry is two lines, so it is the 64 row of the scale and not a
                 52 row stretched by its content (gate 5.5); the trailing word or control centres
                 against the pair rather than sitting on the second line's baseline. */
              <li
                key={method.id}
                className={cx(
                  "ds-row ds-hairline gap-gk-4 items-center px-0",
                  card && "ds-row-comfortable",
                )}
              >
                <div className="gap-gk-1 flex min-w-0 flex-1 flex-col justify-center">
                  <span className="truncate text-[15px] text-[var(--gk-text-primary)]">
                    {card
                      ? t("gymkeePay.portal.cardEnding", {
                          brand: brand ?? t("gymkeePay.portal.cardOther"),
                          last4: card.last4,
                        })
                      : t("gymkeePay.portal.cardOther")}
                  </span>
                  {card && (
                    <span className="gk-num text-[14px] text-[var(--gk-text-secondary)]">
                      {t("gymkeePay.portal.cardExpires", {
                        mm: String(card.expMonth).padStart(2, "0"),
                        yy: String(card.expYear).slice(-2),
                      })}
                    </span>
                  )}
                </div>
                {method.isDefault ? (
                  /* The plain word, not a status: status-as-text 1.13's vocabulary has no
                     "Default" in it, and inventing one at the call site is what its third doNot
                     forbids. A mark beside it would also read as a state the card is IN rather
                     than as the one it is being used as. Canon ask PAY-9 filed. */
                  <span className="flex-none self-center text-[13px] font-medium text-[var(--gk-text-secondary)]">
                    {t("gymkeePay.portal.default")}
                  </span>
                ) : (
                  <Button
                    variant="tertiary"
                    className="ds-pay-hand flex-none self-center"
                    loading={settingDefaultId === method.id}
                    onClick={() => onSetDefault(method.id)}
                  >
                    {t("gymkeePay.portal.useAsDefault")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {adding && (
        <div className="ds-card ds-hairline p-gk-4 gap-gk-4 flex flex-col">
          {stripe && setupClientSecret ? (
            <Elements
              key={coachHex ?? "gymkee"}
              stripe={stripe}
              options={{
                clientSecret: setupClientSecret,
                appearance: stripeAppearance(),
                fonts: [fontSource()],
                locale: stripeLocale(language),
              }}
            >
              <AddCardForm onCancel={onCloseAdd} onAdded={onAdded} />
            </Elements>
          ) : (
            /* The setup intent has not come back yet (or, in the dev fixture, there is no Stripe
               at all). The band stands at the fields' real geometry so nothing jumps when they
               land, and Save card waits until the element says it is complete. */
            <div className="ds-pay-form gap-gk-4 flex flex-col">
              <CardFieldsSkeleton />
              <AddCardActions onCancel={onCloseAdd} disabled />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The setup form. The one orange on the page while it is open is Save card, which is why the
 * section header's Add a card is hidden by the page for as long as this is mounted.
 */
function AddCardForm({
  onCancel,
  onAdded,
}: {
  onCancel: () => void;
  onAdded: () => void;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);
    const { error: setupError } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (setupError) {
      setError(t("gymkeePay.portal.saveCardFailed"));
      setSaving(false);
      return;
    }
    setSaving(false);
    onAdded();
  };

  return (
    <div className="ds-pay-form gap-gk-4 flex flex-col">
      {/* The same band the checkout draws: the fields' real geometry until Stripe says ready. */}
      <CardFieldsBand ready={mounted}>
        <PaymentElement
          options={{
            layout: { type: "tabs" },
            wallets: { applePay: "never", googlePay: "never", link: "never" },
            terms: { card: "never" },
          }}
          onReady={() => setMounted(true)}
          onChange={(event) => setComplete(event.complete)}
        />
      </CardFieldsBand>
      {error && <Alert tone="danger">{error}</Alert>}
      <AddCardActions
        onCancel={onCancel}
        saving={saving}
        disabled={!complete}
        onSave={() => void save()}
      />
    </div>
  );
}

/**
 * The two actions of the add-card region: the safe way out on the left, and the page's one
 * orange on the right for as long as this region is open.
 */
function AddCardActions({
  onCancel,
  onSave,
  saving,
  disabled,
}: {
  onCancel: () => void;
  onSave?: () => void;
  saving?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="gap-gk-3 flex flex-wrap items-center justify-end">
      <Button
        variant="ghost"
        className="ds-pay-hand"
        onClick={onCancel}
        disabled={saving}
      >
        {t("gymkeePay.portal.cancel")}
      </Button>
      <Button
        variant="primary"
        className="ds-pay-hand"
        loading={saving}
        disabled={disabled}
        onClick={onSave}
      >
        {t("gymkeePay.portal.saveCard")}
      </Button>
    </div>
  );
}
